/**
 * Share wrapper — 외부 공유 시트.
 *
 * Notion §2.1.1 — 선물하기 카카오톡/문자/메일 등 외부 채널 공유.
 */
import { isNative, nativeWarn } from "./index";

export interface ShareOptions {
  /** 시트 제목 (Android 만 표시) */
  dialogTitle?: string;
  /** 공유 컨텐츠 제목 */
  title?: string;
  /** 본문 텍스트 */
  text?: string;
  /** 공유할 URL (preview 가능) */
  url?: string;
}

export interface ShareResult {
  /** 공유 완료 (사용자가 앱 선택했음) */
  shared: boolean;
  /** 어느 앱으로 공유했는지 (Android 만 제공) */
  activityType?: string;
}

export async function share(options: ShareOptions): Promise<ShareResult> {
  if (!isNative()) {
    // 웹 fallback — Web Share API
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: options.title,
          text: options.text,
          url: options.url,
        });
        return { shared: true };
      } catch (err) {
        nativeWarn("share:web", err);
      }
    }
    return { shared: false };
  }
  try {
    const { Share } = await import("@capacitor/share");
    const result = await Share.share({
      title: options.title,
      text: options.text,
      url: options.url,
      dialogTitle: options.dialogTitle,
    });
    return {
      shared: true,
      activityType: result.activityType,
    };
  } catch (err) {
    nativeWarn("share:native", err);
    return { shared: false };
  }
}

/**
 * Gift deep link 도메인 — 환경별 분기.
 *   dev  = dev.peeka.app
 *   prod = peeka.app (2026-09-15 apex 로 이동 — app.peeka.app 은 앱 안내 호스트가 됐다)
 * VITE_GIFT_LINK_DOMAIN(.env.{dev,prod}) 로 주입. 미설정 시 dev 도메인 fallback.
 *
 * ⚠ 이 값은 AndroidManifest 의 App Link host(giftAppLinkHost) / iOS associated-domains /
 *   capacitor.config 의 server.hostname 과 반드시 동일해야 링크 클릭 시 앱이 열린다.
 */
const GIFT_LINK_DOMAIN =
  (import.meta.env.VITE_GIFT_LINK_DOMAIN as string | undefined)?.trim() ||
  "peeka.app";

/**
 * 로컬 개발 호스트 여부 — true 면 http + 포트 그대로 사용한다.
 * (127.0.0.1 / localhost 등은 App Link 검증이 불가능하므로 웹 폴백 테스트 전용.)
 */
function isLocalHost(domain: string): boolean {
  return /^(localhost|127\.0\.0\.1|0\.0\.0\.0|10\.0\.2\.2)(:\d+)?$/.test(domain);
}

/**
 * App Link / Universal Link 공유 URL 빌더 — path (예: "/invite/CODE") 를 받아
 * 배포 도메인 기준 절대 URL 을 만든다. (앱 미설치자는 웹 폴백으로 진입.)
 * 로컬 도메인(127.0.0.1:5175 등)은 http, 그 외 배포 도메인은 https.
 */
export function buildAppLinkUrl(path: string): string {
  const scheme = isLocalHost(GIFT_LINK_DOMAIN) ? "http" : "https";
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${scheme}://${GIFT_LINK_DOMAIN}${normalized}`;
}

export async function canShare(): Promise<boolean> {
  if (!isNative()) {
    return typeof navigator !== "undefined" && !!navigator.share;
  }
  try {
    const { Share } = await import("@capacitor/share");
    const result = await Share.canShare();
    return result.value;
  } catch (err) {
    nativeWarn("share:canShare", err);
    return false;
  }
}
